/* eslint-disable max-lines */
import { Alert, Col, Modal, Row, Tabs, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { BackButton } from 'src/components/BackButton/BackButton';
import { Button } from 'src/components/Button/Button';
import { DataView } from 'src/components/DataView/DataView';
import { DetailsPage } from 'src/components/FormPages/DetailsPage';
import { LoanApplicationStatusView } from 'src/components/LoanApplicationStatusView/LoanApplicationStatusView';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { AssignApplicationDialog } from 'src/pages/LoanApplicationDetailsPage/components/AssignPartnerDialog';
import { DeclineApplicationDialog } from 'src/pages/LoanApplicationDetailsPage/components/DeclineApplicationDialog';
import { ReassignApplicationDialog } from 'src/pages/LoanApplicationDetailsPage/components/ReassignApplicationDialog';
import { LoanApplicationCommentsTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationCommentsTab';
import { LoanApplicationDeclineReasonsTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationDeclineReasonsTab';
import { LoanApplicationGeneralInfoTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationGeneralInfoTab';
import { LoanApplicationMerchantSnapshotTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationMerchantSnapshotTab';
import { LoanApplicationReassignmentReasonsTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationReassignmentReasonsTab';
import { LoanApplicationRepaymentsTab } from 'src/pages/LoanApplicationDetailsPage/tabs/LoanApplicationRepaymentsTab';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { ApplicationStatus } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore.types';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import styled from 'styled-components';

// eslint-disable-next-line complexity
export const LoanApplicationDetailsPage = observer(() => {
    const {
        params: { id },
    } = useRouteMatch<{ id: string }>();

    useEffect(() => {
        return () => {
            LoanApplicationsStore.repaidLoader.reset();
        };
    }, []);

    const { isLoadingResources } = useLoadResources([
        InstallmentOptionResourceStore,
        PaymentFreequencyResourceStore,
    ]);

    const application = LoanApplicationsStore.currentItem;

    const finalDeclineReason = application?.finalDeclineReason;
    const finallyDeclinedAt = application?.finalDeclineAt;

    const showDeclineMessage =
        application?.status === ApplicationStatus.DECLINED &&
        finalDeclineReason;

    // Mutating workflow buttons and the Reassignment Reasons tab are
    // CreditOfficer-only (can.manage). Sales / Financial officers get read +
    // export access, but no workflow actions.
    const canManage = UserStore.can.manage.loanApplications;

    const showDeclineButton =
        canManage &&
        application?.status &&
        application.status !== ApplicationStatus.DECLINED &&
        application.status !== ApplicationStatus.ACTIVE &&
        application.status !== ApplicationStatus.FULLY_PAID;

    const showAssignButton =
        canManage &&
        application &&
        application.status === ApplicationStatus.PENDING_ASSIGNMENT;

    const showReassignButton =
        canManage &&
        application &&
        (application.status === ApplicationStatus.RETURNED_FOR_ASSIGNMENT ||
            application.status === ApplicationStatus.ASSIGNED);

    const showRepaidButton =
        canManage &&
        application &&
        application.status === ApplicationStatus.ACTIVE;

    return (
        <DetailsPage
            pageName="loan-application-details"
            store={LoanApplicationsStore as unknown as BasicStore<Entity>}
            isLoading={isLoadingResources}
            headerTopPanel={
                <BackButton to={EntityLinks.loanApplications.list?.()} />
            }
        >
            <Row gutter={16} align="middle">
                <Col flex={1}>
                    <Typography.Title level={3}>
                        {application?.number}
                    </Typography.Title>
                </Col>

                {application?.status && (
                    <Col>
                        <LoanApplicationStatusView
                            status={application.status}
                        />
                    </Col>
                )}

                {showAssignButton && (
                    <Col>
                        <Button
                            type="primary"
                            onClick={() => {
                                AssignApplicationDialog.render({
                                    applicationId: application.id!,
                                    onSuccess: () => {
                                        LoanApplicationsStore.loadItem(id);
                                    },
                                });
                            }}
                        >
                            {t('LoanApplication.Actions.Assign')}
                        </Button>
                    </Col>
                )}

                {showReassignButton && (
                    <Col>
                        <Button
                            type="primary"
                            onClick={() => {
                                ReassignApplicationDialog.render({
                                    applicationId: application.id!,
                                    withReassignmentReason:
                                        application.status ===
                                        ApplicationStatus.ASSIGNED,
                                    onSuccess: () => {
                                        LoanApplicationsStore.loadItem(id);
                                    },
                                });
                            }}
                        >
                            {t('LoanApplication.Actions.Reassign')}
                        </Button>
                    </Col>
                )}

                {showDeclineButton && (
                    <Col>
                        <Button
                            className="btn-danger"
                            onClick={() => {
                                DeclineApplicationDialog.render({
                                    applicationId: application.id!,
                                    onSuccess: () => {
                                        LoanApplicationsStore.loadItem(id);
                                    },
                                });
                            }}
                        >
                            {t('LoanApplication.Actions.Decline')}
                        </Button>
                    </Col>
                )}

                {showRepaidButton && (
                    <Col>
                        <Button
                            type="primary"
                            loading={
                                LoanApplicationsStore.repaidLoader.isLoading
                            }
                            onClick={() => {
                                Modal.confirm({
                                    title: t(
                                        'LoanApplication.Actions.MarkAsFullyPaid.Confirm.Title',
                                    ),
                                    content: t(
                                        'LoanApplication.Actions.MarkAsFullyPaid.Confirm.Text',
                                        {
                                            applicationNumber:
                                                application.number,
                                        },
                                    ),
                                    onOk: async () => {
                                        await LoanApplicationsStore.repaid(
                                            application.id!,
                                        );
                                        if (
                                            !LoanApplicationsStore.repaidLoader
                                                .hasError
                                        ) {
                                            LoanApplicationsStore.loadItem(
                                                application.id!,
                                            );
                                        }
                                    },
                                });
                            }}
                        >
                            {t('LoanApplication.Actions.MarkAsFullyPaid')}
                        </Button>
                    </Col>
                )}

                {showDeclineMessage && (
                    <Col span={24}>
                        <Alert
                            type="error"
                            message={
                                <StyledWrapper>
                                    <DataView
                                        label={t(
                                            'LoanApplication.DeclineReasons.FinalDeclineReason',
                                        )}
                                        value={finalDeclineReason}
                                    />
                                    <DataView
                                        label={t(
                                            'LoanApplication.DeclineReasons.FinallyDeclinedAt',
                                        )}
                                        value={finallyDeclinedAt}
                                        formatValue="dateTimeSeconds"
                                    />
                                </StyledWrapper>
                            }
                        />
                    </Col>
                )}
                <Col span={24}>
                    <Tabs
                        items={[
                            {
                                key: 'general-information',
                                label: t(
                                    'LoanApplication.Tab.GeneralInformation',
                                ),
                                children: (
                                    <LoanApplicationGeneralInfoTab
                                        application={application}
                                    />
                                ),
                            },
                            {
                                key: 'merchant-snapshot',
                                label: t(
                                    'LoanApplication.Tab.MerchantSnapshot',
                                ),
                                children: (
                                    <LoanApplicationMerchantSnapshotTab />
                                ),
                            },
                            {
                                key: 'decline-reasons',
                                label: t('LoanApplication.Tab.DeclineReasons'),
                                children: (
                                    <LoanApplicationDeclineReasonsTab
                                        application={application}
                                    />
                                ),
                            },
                            ...(canManage
                                ? [
                                      {
                                          key: 'reassignment-reasons',
                                          label: t(
                                              'LoanApplication.Tab.ReassignmentReasons',
                                          ),
                                          children: (
                                              <LoanApplicationReassignmentReasonsTab
                                                  application={application}
                                              />
                                          ),
                                      },
                                  ]
                                : []),
                            {
                                key: 'repayments',
                                label: t('LoanApplication.Tab.Repayments'),
                                children: (
                                    <LoanApplicationRepaymentsTab
                                        application={application}
                                    />
                                ),
                            },
                            {
                                key: 'comments',
                                label: t('LoanApplication.Tab.Comments'),
                                children: (
                                    <LoanApplicationCommentsTab
                                        application={application}
                                    />
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </DetailsPage>
    );
});

const StyledWrapper = styled.div`
    .data-view {
        display: flex;
        gap: 10px;
    }
`;
